import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { leadsApi } from "./lib/leadsApi";
import { Search } from "lucide-react";
import { Sidebar } from "./components/layout/Sidebar";
import { TopBar } from "./components/layout/TopBar";
import { Toast } from "./components/shared/Toast";
import { PageHeader } from "./components/shared/PageHeader";
import { Card } from "./components/shared/Card";
import { ConfirmDialog } from "./components/shared/ConfirmDialog";
import { NAV } from "./lib/rbac";
import { AppDataProvider, useAppData } from "./lib/AppDataContext";
import { useNav } from "./lib/NavContext";
import { useAuth } from "./lib/AuthContext";

// Batch 2 pages
import { LocalitiesView } from "./pages/locations/LocalitiesView";
import { BuildersView } from "./pages/builders/BuildersView";
import { SalesTeamView } from "./pages/sales-team/SalesTeamView";
import { SiteContactSettings } from "./pages/cms/SiteContactSettings";

// Batch 3a pages
import { PropertiesView } from "./pages/properties/PropertiesView";
import { CitiesView } from "./pages/locations/CitiesView";

// Batch 3b pages
import { SalesTeamPerformance } from "./pages/sales-team/SalesTeamPerformance";
import { MyAttendanceView } from "./pages/attendance/MyAttendanceView";
import { TodaysRosterView } from "./pages/attendance/TodaysRosterView";
import { AttendanceCalendarView } from "./pages/attendance/AttendanceCalendarView";
import { AttendanceHistoryView } from "./pages/attendance/AttendanceHistoryView";

// Batch 4a / 4b — Leads system
import { Dashboard } from "./pages/leads/Dashboard";
import { LeadsList } from "./pages/leads/LeadsList";
import { LeadDetail } from "./pages/leads/LeadDetail";
import { LeadSettings } from "./pages/leads/LeadSettings";

// Batch 5a — Billing module
const BillingModule = lazy(() => import("./pages/billing/BillingModule").then((m) => ({ default: m.BillingModule })));

// Batch 6a — Interior module
const InteriorModule = lazy(() => import("./pages/interior/InteriorModule").then((m) => ({ default: m.InteriorModule })));

// Batch 6b — Blog
import { BlogView } from "./pages/blog/BlogView";

// Batch 6c — CMS suite
const CMSModule = lazy(() => import("./pages/cms/CMSModule").then((m) => ({ default: m.CMSModule })));

// Batch 6d — Settings (wrap-up)
import { SettingsView } from "./pages/settings/SettingsView";

// Batch 7 — SEO · Media · Analytics
const SEOView = lazy(() => import("./pages/seo/SEOView").then((m) => ({ default: m.SEOView })));
const MediaLibrary = lazy(() => import("./pages/media/MediaLibrary").then((m) => ({ default: m.MediaLibrary })));
const AnalyticsView = lazy(() => import("./pages/analytics/AnalyticsView").then((m) => ({ default: m.AnalyticsView })));

export default function AdminShell() {
  return (
    <AppDataProvider>
      <Shell />
    </AppDataProvider>
  );
}

function Shell() {
  // Phase 1 wiring:
  //  - active/setActive come from NavContext (URL ↔ state sync) when wrapped
  //    in a NavProvider by AdminPanelRouted. Falls back to local state if not.
  //  - role comes from the authenticated user via AuthContext.
  //    setRole is a no-op now; role changes happen via the User Management
  //    screen (Settings → Users), which calls PATCH /users/:id.
  const nav = useNav();
  const { user } = useAuth();
  const [localActive, setLocalActive] = useState("dashboard");
  const active = nav ? nav.active : localActive;
  const setActive = nav ? nav.setActive : setLocalActive;
  const role = user?.role || "employee";
  const setRole = () => {};

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Section the user tried to switch to while a form was dirty.
  const [pendingSection, setPendingSection] = useState(null);
  // openLead lifts the lead-detail state to the shell so both LeadsList
  // and the Dashboard's New-Leads / Today's-Schedule cards can open it.
  const [openLead, setOpenLeadState] = useState(null);
  // Persist the open lead across refresh: store its id, restore on mount.
  const setOpenLead = (leadOrNull) => {
    setOpenLeadState(leadOrNull);
    try {
      if (leadOrNull && leadOrNull.id) localStorage.setItem("leads:openLeadId", leadOrNull.id);
      else localStorage.removeItem("leads:openLeadId");
    } catch { /* ignore */ }
  };
  useEffect(() => {
    let alive = true;
    let storedId = null;
    try { storedId = localStorage.getItem("leads:openLeadId"); } catch { /* ignore */ }
    if (storedId) {
      leadsApi.getOne(storedId)
        .then((full) => { if (alive && full) setOpenLeadState(full); })
        .catch(() => { try { localStorage.removeItem("leads:openLeadId"); } catch { /* ignore */ } });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open a specific lead from anywhere (e.g. the notifications dropdown).
  // Components dispatch window "abhighar:open-lead" with { leadId }; we
  // switch to the leads section and open that lead via the same getOne +
  // setOpenLead path the refresh-restore uses (so it also survives reload).
  useEffect(() => {
    const handler = (e) => {
      const leadId = e?.detail?.leadId;
      if (!leadId) return;
      setActive("leads.all");
      leadsApi.getOne(leadId)
        .then((full) => { if (full) setOpenLead(full); })
        .catch(() => { /* ignore */ });
    };
    window.addEventListener("abhighar:open-lead", handler);
    return () => window.removeEventListener("abhighar:open-lead", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { updateLead, statusVersion, dirtyForm, setDirtyForm } = useAppData();

  const allowedIds = useMemo(() => {
    const ids = new Set();
    NAV.forEach((n) => {
      if (n.roles.includes(role)) {
        ids.add(n.id);
        n.children?.forEach((c) => c.roles.includes(role) && ids.add(c.id));
      }
    });
    return ids;
  }, [role]);
  const safeActive = allowedIds.has(active) ? active : "dashboard";

  const renderScreen = () => {
    // Lead detail takes over whenever a lead is open, regardless of the
    // active sidebar route. Closing via Back returns to the route the
    // user was on.
    if (openLead) {
      return (
        <LeadDetail
          lead={openLead}
          role={role}
          statusVersion={statusVersion}
          onBack={() => setOpenLead(null)}
          onUpdate={(patched) => {
            updateLead(patched);
            setOpenLead(patched);
          }}
        />
      );
    }

    const a = safeActive;
    if (a === "dashboard") return <Dashboard role={role} />;

    // Batch 2 routes
    if (a === "locations.localities") return <LocalitiesView />;
    if (a === "builders") return <BuildersView />;
    if (a === "cms.contact") return <SiteContactSettings />;

    // Employee viewing "sales_team" lands on Performance (their own KPIs).
    // Super_admin/admin landing on "sales_team" goes to the team list.
    if (a === "sales_team.performance" || (a === "sales_team" && role === "employee")) {
      return <SalesTeamPerformance role={role} />;
    }
    if (a === "sales_team" || a === "sales_team.all") return <SalesTeamView />;

    if (a === "properties.all") return <PropertiesView key="all" view="all" role={role} />;
    if (a === "properties.new") return <PropertiesView key="new" view="all" startAdding role={role} />;
    if (a === "properties.drafts") return <PropertiesView key="drafts" view="drafts" role={role} />;
    if (a === "locations.cities") return <CitiesView />;

    if (a === "my_attendance") return <MyAttendanceView />;
    if (a === "attendance" || a === "attendance.today") return <TodaysRosterView />;
    if (a === "attendance.calendar") return <AttendanceCalendarView />;
    if (a === "attendance.history") return <AttendanceHistoryView />;

    // Batch 4b — Leads list + Settings
    if (a === "leads.settings") return <LeadSettings />;
    if (a.startsWith("leads")) return <LeadsList role={role} onOpen={setOpenLead} />;

    // Batch 5a — Billing routes
    if (a === "billing" || a.startsWith("billing")) return <BillingModule subRoute={a} />;

    // Batch 6a — Interior routes
    if (a === "interior" || a.startsWith("interior")) return <InteriorModule subRoute={a} role={role} />;

    // Batch 6b — Blog
    if (a === "blog") return <BlogView role={role} />;

    // Batch 6c — CMS routes
    if (a === "cms" || a.startsWith("cms")) return <CMSModule subRoute={a} />;

    // Batch 6d — Settings
    if (a === "settings") return <SettingsView />;

    // Batch 7 — SEO · Media · Analytics
    if (a === "seo")       return <SEOView />;
    if (a === "media")     return <MediaLibrary />;
    if (a === "analytics") return <AnalyticsView />;

    return <BatchPlaceholder route={a} />;
  };

  return (
    <div
      className="flex h-screen bg-[#faf9f5] text-slate-900"
      style={{
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
      }}
    >
      <Toast />
      <Sidebar
        role={role}
        active={safeActive}
        setActive={(id) => {
          if (dirtyForm && id !== safeActive) { setPendingSection(id); return; }
          setActive(id); setOpenLead(null);
        }}
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onOpenSidebar={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto p-3 sm:p-5 lg:p-6">
          <Suspense fallback={
            <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
          }>
            {renderScreen()}
          </Suspense>
        </main>
      </div>
      <ConfirmDialog
        open={!!pendingSection}
        title="Discard unsaved changes?"
        message="You have unsaved changes in this form. Leaving will discard them."
        confirmLabel="Discard & leave"
        onConfirm={() => {
          setDirtyForm(false);
          setActive(pendingSection);
          setOpenLead(null);
          setPendingSection(null);
        }}
        onCancel={() => setPendingSection(null)}
      />
    </div>
  );
}

function BatchPlaceholder({ route }) {
  /* Generic catch-all for any nav route not explicitly wired above.
     Every route in NAV currently has a real screen — this only fires
     for typos or routes added to NAV before their screen ships. */
  return (
    <div>
      <PageHeader title="Coming soon" subtitle={`Route: ${route}`} />
      <Card className="p-12 grid place-items-center text-center">
        <div className="w-12 h-12 rounded-xl bg-slate-100 grid place-items-center mb-3">
          <Search className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-sm font-medium text-slate-700">{route}</p>
        <p className="text-xs text-slate-400 mt-1 max-w-sm">
          This nav entry has no view registered yet.
        </p>
      </Card>
    </div>
  );
}
